import { Druckformular, Fahrzeug, Personal, ReportElement, ReportElementType, BarcodeType, Auftragsposition, TextAlignType } from 'src/app/api/model/swagger-model';
import { ZplImage } from './zpl-image';
import { ImageHelper } from 'src/app/api/helper/image-helper';
import { AuftragEx, DruckOptions } from 'src/app/api/model/model';
import { Utils } from 'src/app/api/helper/utils';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { DruckerHelper } from './drucker-helper';
import QRCode from 'qrcode'
import { DATAMatrix } from './datamatrix';

import moment from 'moment';
import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';
import { DruckerKonfiguration } from 'src/app/api/helper/app.config';

const log = new Logger('ZebraDrucker');

export class EscPosDrucker {

    private encoder: ReceiptPrinterEncoder;

    constructor(
        private druckformular: Druckformular,
        private auftrag: AuftragEx,
        private fahrzeug: Fahrzeug,
        private fahrer: Personal,
        private drucker: DruckerKonfiguration) {

        if (!druckformular) {
            throw new Error('Parameter druckformular darf nicht null sein')
        }
    }

    static getEscPosModels(): { id: string, name: string }[] {
        return [...ReceiptPrinterEncoder.printerModels];
    }

    async erstelleBelegCode(options: DruckOptions): Promise<Uint8Array> {
        if (!this.auftrag) {
            throw new Error('Parameter auftrag darf nicht null sein')
        }

        const druckformular = this.druckformular;
        const auftrag = this.auftrag;
        const now = moment();

        if (!auftrag.Abholadresse) {
            auftrag.Abholadresse = {};
        }

        if (!auftrag.Erzeugeradresse) {
            auftrag.Erzeugeradresse = {};
        }

        if (!auftrag.Verwerteradresse) {
            auftrag.Verwerteradresse = {};
        }

        if (!this.fahrzeug) {
            this.fahrzeug = {};
        }

        if (!this.fahrer) {
            this.fahrer = {};
        }

        // siehe: https://github.com/NielsLeenheer/ReceiptPrinterEncoder/blob/main/documentation/commands.md

        // siehe: https://github.com/NielsLeenheer/ReceiptPrinterEncoder/blob/main/documentation/configuration.md
        const opts: any = {
            // printerModel: 'epson-tm-t88vi',
            language: 'esc-pos',
            // feedBeforeCut: 4,
        };

        if (this.drucker.model) {
            opts.printerModel = this.drucker.model;
        }

        if (this.drucker.width) {
            opts.columns = Utils.parseInt(this.drucker.width);
        }

        this.encoder = new ReceiptPrinterEncoder(opts);

        this.encoder.initialize()

        const platzhalter = DruckerHelper.erstellePlatzhalter(auftrag, this.fahrer, this.fahrzeug, null);

        if (druckformular.Header && druckformular.Header.Model && druckformular.Header.Model.Elements) {
            this.sortiereElemente(druckformular.Header.Model.Elements);

            for (const reportElement of druckformular.Header.Model.Elements) {
                await this.erstelleLinesFuerReportElement(reportElement, platzhalter);
            }
        }

        if (auftrag.Positionen && druckformular.Details && druckformular.Details.Model) {
            for (const position of auftrag.Positionen) {
                if (position.Menge) {
                    const posPlatzhalter = DruckerHelper.erstellePlatzhalter(auftrag, this.fahrer, this.fahrzeug, position);

                    this.sortiereElemente(druckformular.Details.Model.Elements);

                    for (const reportElement of druckformular.Details.Model.Elements) {
                        // Bei "Wiegungen im Ausdruck = alle" wird ein Element mit Wiegungs-Platzhaltern
                        // einmal je Wiegung gedruckt.
                        const platzhalterListe = DruckerHelper.getPlatzhalterListeFuerElement(druckformular, reportElement, posPlatzhalter, position);

                        for (const p of platzhalterListe) {
                            await this.erstelleLinesFuerReportElement(reportElement, p, position)
                        }
                    }
                }
            }
        }

        if (druckformular.Footer && druckformular.Footer.Model && druckformular.Footer.Model.Elements) {
            this.sortiereElemente(druckformular.Footer.Model.Elements);

            for (const reportElement of druckformular.Footer.Model.Elements) {
                await this.erstelleLinesFuerReportElement(reportElement, platzhalter)
            }
        }

        // if (options) {
        //     if (options.anzahl > 1 && options.anzahl <= 10) {
        //         lines.push('^PQ' + options.anzahl);
        //     }
        // }

        // lines.push('^XZ');

        // // Label-Länge und Breite am Anfang setzen
        // const labelLength = this.deltaY + this.druckformular.HomeY;
        // const labelWidth = this.druckformular.LabelWidth * this.druckformular.Dpmm;
        // lines.unshift(`^XA^LL${labelLength}^PW${labelWidth}^XZ`);

        // if (this.druckformular.ZplStart) {
        //     lines.unshift(this.druckformular.ZplStart);
        // }

        // if (this.druckformular.ZplEnde) {
        //     lines.push(this.druckformular.ZplEnde);
        // }

        // const zpl = lines.join('\r\n');

        // log?.debug(zpl);

        // TODO: Hinweistexte
        // TODO: Unterschriften

        return this.encoder.encode();
    }

    sortiereElemente(elemente: ReportElement[]) {
        elemente.sort((a, b) => {
            let result = 0;

            if (typeof (a.Y) === 'number' && typeof (b.Y) === 'number') {
                result = a.Y - b.Y;

                if (result == 0) {
                    if (typeof (a.X) === 'number' && typeof (b.X) === 'number') {
                        result = a.X - b.X;
                    }
                }
            }

            return result;
        })
    }

    private async erstelleLinesFuerReportElement(e: ReportElement, platzhalter: any, position: Auftragsposition = null): Promise<void> {
        if (!DruckerHelper.isDruckbedingungOk(e, this.auftrag, this.fahrer, this.fahrzeug, platzhalter, position)) {
            return;
        }

        const text = this.formatTextbockText(e.Content, platzhalter);

        if (!text) {
            return;
        }

        // Schriftart
        if (e.Font) {
            if (e.Font === 'A') {
                // Für den TM-U295-Drucker muss hier der Print-Mode umgeschaltet werden
                // ESC ! n  -- Select print mode(s)
                // A = 5x7 Font
                this.encoder.raw([ 0x1b, 0x21, 0x00 ]); 
            } else if (e.Font === 'B') {
                // B = 7x7 Font
                this.encoder.raw([ 0x1b, 0x21, 0x01 ]); 
            }
            // this.encoder.font(e.Font);
        }

        // Position
        if (e.TextAlign === TextAlignType.Right) {
            this.encoder.align('right');
        } else if (e.TextAlign === TextAlignType.Justified) {
            this.encoder.align('center');
        } else {
            this.encoder.align('left');
        }

        // Die Breite wird im Designer in Pixeln (oder so ähnlich) angegeben.
        // Für des ESC/POS-Protokoll muss die BReite aber in Zeichen-Breite angegeben werden.
        // Wir teilen hier deshalb durch einen sizeFaktor.
        let sizeFaktor = 8;

        let width = e.Width / sizeFaktor;
        let height = e.Height / sizeFaktor;

        if (width) {
            // Die Breite und Hoehe muss immer ein vielfaches von 8 sein.
            let rest = width % 8;

            if (rest !== 0) {
                width = width + (8 - rest);
            }
        }

        if (height) {
            // Die Breite und Hoehe muss immer ein vielfaches von 8 sein.
            let rest = height % 8;

            if (rest !== 0) {
                height = height + (8 - rest);
            }
        }

        switch (e.Typ) {
            case ReportElementType.Text:
            case ReportElementType.Textblock:
                {
                    // Text
                    let zeilen = Utils.splitZeilen(text);

                    for (const zeile of zeilen) {
                        let txt = Utils.trim(zeile);

                        if (txt.startsWith('===')) {
                            this.encoder.rule({ style: 'double' });
                        } else if (txt.startsWith('---')) {
                            this.encoder.rule({ style: 'single' });
                        } else {
                            // Normaler Text. Hier kein trim. Eventuelle Leerzeichen am Anfang sind gewollt zum Einrücken.
                            this.encoder.line(zeile);
                        }
                    }
                }
                break;

            // ^FO0,60^FDName:^FS
            case ReportElementType.Barcode:
                {
                    // w = module width (default: 2)
                    let w = 2;

                    if (e.ModuleWidth && e.ModuleWidth > 2) {
                        w = e.ModuleWidth;
                    }

                    switch (e.BarcodeType) {
                        // case BarcodeType.Code11:
                        //     this.encoder.barcode(text, 'ean13')
                        //     break;

                        // case BarcodeType.Interleaved2of5:
                        //     this.encoder.barcode(text, 'ean13')
                        //     break;

                        case BarcodeType.Code128:
                            this.encoder.barcode(text, 'code128');
                            break;

                        case BarcodeType.GS1_128:
                            this.encoder.barcode(text, 'code128');
                            break;

                        case BarcodeType.QR:
                        case BarcodeType.QR_Image:
                            this.encoder.qrcode(text);
                            break;
                        // case BarcodeType.DataMatrix:

                        case BarcodeType.Custom:
                            const code = e.CustomCode;
                            this.encoder.barcode(text, code);
                            break;

                        default:
                        case BarcodeType.Code39:
                            this.encoder.barcode(text, 'code39');
                            break;
                    }

                    // lines.push(`^FD${text}^FS`);
                }

                break;

            case ReportElementType.Rect:
                {
                    if (e.Height === 1) {
                        // Linie
                        this.encoder.rule({
                            // The style of the line, either single or double
                            style: 'single',

                            // The width of the line, by default the width of the paper
                            width: width,
                        });
                    } else {
                        // Box
                        this.encoder.box({
                            // The style of the border, either none, single or double
                            style: 'single',

                            // The width of the box including the border, by default the width of the paper
                            width: width,

                            // Space between the left border and the left edge
                            marginLeft: 0,

                            // Space between the right border and the right edge
                            marginRight: 0,

                            // Space between the contents and the left border of the box
                            paddingLeft: 0,

                            // Space between the contents and the right border of the box
                            paddingRight: 0,

                            // The alignment of the text within the box, can be left or right.
                            align: 'left'
                        });
                    }
                }

                break;

            case ReportElementType.Image:
                {
                    let imageDataUrl;

                    switch (e.Content) {
                        case 'unterschrift-erz':
                            imageDataUrl = this.GetUnterschriftBild('ERZ');
                            break;
                        case 'unterschrift-bef':
                            imageDataUrl = this.GetUnterschriftBild('BEF');
                            break;
                        case 'unterschrift-ent':
                            imageDataUrl = this.GetUnterschriftBild('ENT');
                            break;
                        case 'custom':
                            imageDataUrl = e.ImageData;
                            break;
                    };

                    if (imageDataUrl) {
                        let img = new Image();
                        img.src = imageDataUrl;

                        this.encoder.image(img, width, height, 'atkinson');
                    }

                    // TODO: Nur mit plugin derzeit möglich
                    // const t = e.BorderThickness ? e.BorderThickness : 1;
                    // const c = e.LineColor ? e.LineColor : 'B';
                    // const r = e.CornerRounding ? e.CornerRounding : 0;

                    // lines.push(`^GB${e.Width},${e.Height},${t},${c},${r}^FS`);
                }

                break;

            default:
                // TODO
                break;
        }
    }

    private GetUnterschriftBild(rolle: string) {
        if (!this.auftrag.Unterschriften) {
            return null;
        }

        const unterschrift = this.auftrag.Unterschriften.find(p => p.Typ === rolle);

        if (unterschrift) {
            const bild = unterschrift.Bild;

            if (bild) {
                if (bild.startsWith('data:')) {
                    return bild;
                }
            } else if (unterschrift.Guid) {
                // TODO
            }
        }

        return null;
    }

    private formatTextbockText(text: string, platzhalter: any): string {
        if (!text) {
            text = '';
        }

        if (platzhalter && text && text.indexOf('{') >= 0) {
            for (const key in platzhalter) {
                if (platzhalter.hasOwnProperty(key)) {
                    let value = platzhalter[key];

                    if (typeof (value) === 'undefined' || value == null || value === 'undefined') {
                        value = '';
                    }

                    text = this.ersetzePlatzhalter(text, key, value)
                }
            }
        }

        // Unicode-Zeichen ersetzen
        // let result = '';

        // for (let i = 0; i < text.length; i++) {
        //     const c = text.charCodeAt(i);

        //     if (c === 13) {
        //         // ignore
        //     } else if (c === 10) {
        //         result += '\\&';
        //     } else if (c <= 255) {
        //         result += text[i];
        //     } else {
        //         // Escape (TODO)
        //         result += text[i];
        //     }
        // }

        return text;
    }

    private ersetzePlatzhalter(str: string, platzhalter: string, wert: any): string {
        return this.ersetze(str, '{' + platzhalter + '}', wert);
    }

    private ersetze(str: string, suchwert: string, wert: any): string {
        let result = str;
        let ttl = 10;

        do {
            if (wert === null || typeof (wert) === 'undefined') {
                wert = '';
            }

            result = str.replace(suchwert, wert);

            if (result === str) {
                // Nichts mehr ersetzt
                break;
            }
        } while (ttl-- > 0);

        return result;
    }

}